import React from 'react'

export function hasMatchResult(match) {
  return Number.isInteger(match.homeGoals) && Number.isInteger(match.awayGoals)
}

export function getGuessHits(guess, match) {
  if (!hasMatchResult(match)) return null
  const homeHit = guess.homeGoals === match.homeGoals
  const awayHit = guess.awayGoals === match.awayGoals
  return { homeHit, awayHit, exactHit: homeHit && awayHit }
}

export function ExactHitBadge({ compact = false }) {
  return (
    <span
      className={`worldcup-exact-hit-badge${compact ? ' worldcup-exact-hit-badge--compact' : ''}`}
      title="Præcis resultat"
    >
      <svg
        className="worldcup-exact-hit-icon"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M8 1.2l1.55 4.74h5.01L10.9 9.01l1.55 4.74L8 11.68 3.55 13.75 5.1 9.01 1.44 5.94h5.01L8 1.2z"
          fill="currentColor"
        />
      </svg>
      <span>Præcis</span>
    </span>
  )
}

function ScoreDigits({ home, away, hits, exactHighlight }) {
  return (
    <div
      className={`worldcup-colored-score-values${
        exactHighlight && hits?.exactHit ? ' worldcup-colored-score-values--exact' : ''
      }`}
    >
      <span
        className={`worldcup-score-digit${hits.homeHit ? ' worldcup-score-digit--hit' : ' worldcup-score-digit--miss'}`}
      >
        {home}
      </span>
      <span className="worldcup-score-sep" aria-hidden="true">
        -
      </span>
      <span
        className={`worldcup-score-digit${hits.awayHit ? ' worldcup-score-digit--hit' : ' worldcup-score-digit--miss'}`}
      >
        {away}
      </span>
    </div>
  )
}

export function ColoredScore({ home, away, hits, label }) {
  if (!hits) {
    return (
      <div className="worldcup-colored-score">
        <span className="worldcup-colored-score-label">{label}</span>
        <span className="worldcup-match-score worldcup-match-score--guess">
          {home}-{away}
        </span>
      </div>
    )
  }

  return (
    <div className="worldcup-colored-score">
      <span className="worldcup-colored-score-label">{label}</span>
      <ScoreDigits home={home} away={away} hits={hits} exactHighlight />
    </div>
  )
}

const MATCH_SCORE_LABELS = {
  guess: 'Dit gæt',
  result: 'Resultat',
}

export function InlineColoredGuessScore({ guess, match }) {
  const hits = getGuessHits(guess, match)
  if (!hits) {
    return (
      <span className="worldcup-match-score worldcup-match-score--guess">
        {guess.homeGoals}-{guess.awayGoals}
      </span>
    )
  }

  return (
    <ScoreDigits
      home={guess.homeGoals}
      away={guess.awayGoals}
      hits={hits}
      exactHighlight
    />
  )
}

export function MatchGuessListRow({ guess, match, isFinished }) {
  const hasResult = hasMatchResult(match)
  const hits = hasResult ? getGuessHits(guess, match) : null
  const showOutcome = isFinished && hasResult

  return (
    <div
      className={`worldcup-match-guess-row${
        showOutcome
          ? hits?.exactHit
            ? ' worldcup-match-guess-row--perfect'
            : guess.pointsAwarded > 0
              ? ' worldcup-match-guess-row--earned'
              : ' worldcup-match-guess-row--miss'
          : ''
      }`}
    >
      <div className="worldcup-match-guess-user">
        <span className="worldcup-match-guess-name">{guess.userName || 'Ukendt'}</span>
        <div className="worldcup-match-guess-user-badges">
          {hits?.exactHit ? <ExactHitBadge compact /> : null}
          {guess.isJoker ? <span className="worldcup-match-guess-joker">Joker</span> : null}
        </div>
      </div>
      <div className="worldcup-match-guess-meta">
        <InlineColoredGuessScore guess={guess} match={match} />
        {showOutcome ? (
          <span
            className={`worldcup-match-points worldcup-my-guess-points${
              guess.pointsAwarded > 0
                ? ' worldcup-my-guess-points--earned'
                : ' worldcup-my-guess-points--zero'
            }`}
          >
            {guess.pointsAwarded} pt
          </span>
        ) : (
          <span className="worldcup-match-points worldcup-my-guess-points worldcup-my-guess-points--pending">
            Afventer
          </span>
        )}
      </div>
    </div>
  )
}

export function CompactMatchScoreLine({ home, away, hits, variant }) {
  const label = MATCH_SCORE_LABELS[variant]
  const scoreContent = !hits ? (
    <span className={`worldcup-match-score worldcup-match-score--${variant}`}>
      {home}-{away}
    </span>
  ) : (
    <ScoreDigits
      home={home}
      away={away}
      hits={hits}
      exactHighlight={variant === 'guess'}
    />
  )

  return (
    <div
      className={`worldcup-match-score-line worldcup-match-score-line--${variant}${
        variant === 'guess' && hits?.exactHit ? ' worldcup-match-score-line--perfect' : ''
      }`}
    >
      <span className="worldcup-match-score-line-label">{label}</span>
      <div className="worldcup-match-score-line-values">{scoreContent}</div>
    </div>
  )
}
